// components/ProposeTokenMint.js
import React, { useState, useEffect } from 'react';
import Web3 from 'web3';
import { contractABI, contractAddress } from '../../contractConfig';
import '../../token.css';

const ProposeTokenMint = () => {
  const [tokenName, setTokenName] = useState('');
  const [tokenDescription, setTokenDescription] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [message, setMessage] = useState('');
  const [assetMetaIPFS, setAssetMetaIPFS] = useState('');
  const [numTokens, setNumTokens] = useState(1);
  const [busy, setBusy] = useState(false);
  const [authorisedProduct, setAuthorisedProduct] = useState('');
  const [authorisedQuantity, setAuthorisedQuantity] = useState('');
  const [quantityUnit, setQuantityUnit] = useState('');
  const [validFrom, setValidFrom] = useState('');
  const [validUntil, setValidUntil] = useState('');

  const handleTokenNameChange = (e) => setTokenName(e.target.value);
  const handleTokenDescriptionChange = (e) => setTokenDescription(e.target.value);
  const handleImageChange = (event) => {
    const file = event.target.files?.[0];

    if (!file) return;

    const validTypes = ['image/jpeg', 'image/png'];
    const maxSize = 5 * 1024 * 1024; // 5 MB

    if (!validTypes.includes(file.type)) {
        setImageFile(null);
        setImagePreview('');
        setMessage('Invalid file type. Only JPEG and PNG are allowed.');
        event.target.value = '';
        return;
    }

    if (file.size > maxSize) {
        setImageFile(null);
        setImagePreview('');
        setMessage('Image size must not exceed 5 MB.');
        event.target.value = '';
        return;
    }

    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    setAssetMetaIPFS(''); // Previous metadata no longer matches the image
    setMessage('');
  };

  // Add Cleanup
  useEffect(() => {
    return () => {
        if (imagePreview) {
            URL.revokeObjectURL(imagePreview);
        }
    };
  }, [imagePreview]);

  const handleNumTokensChange = (e) => setNumTokens(e.target.value);

  // ---- Wallet connect ----
  const ensureWallet = async () => {
    if (!window.ethereum) throw new Error('MetaMask not installed');
    let accounts = await window.ethereum.request({ method: 'eth_accounts' });
    if (!accounts || accounts.length === 0) {
      accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
    }
    const web3 = new Web3(window.ethereum);
    return { web3, account: accounts[0] };
  };

  // ---- Upload helpers ----
  const uploadToIPFS = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch('/api/upload', { method: 'POST', body: formData });
    if (!response.ok) throw new Error('Failed to upload file to IPFS');
    const data = await response.json();
    return data.ipfsUri.replace('ipfs://', '');
  };

  const uploadMetadataToIPFS = async (metadata) => {
    const response = await fetch('/api/uploadMetadata', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(metadata),
    });
    if (!response.ok) throw new Error('Failed to upload metadata to IPFS');
    const data = await response.json();
    return data.ipfsUri.replace('ipfs://', '');
  };

  const generateAssetMetaIPFS = async () => {
    try {
        if (
          !tokenName.trim() ||
          !tokenDescription.trim() ||
          !imageFile ||
          !authorisedProduct.trim() ||
          !authorisedQuantity ||
          !quantityUnit ||
          !validFrom ||
          !validUntil
        ) {
          alert(
            'Please complete all licence information and upload an image.'
          );
          return;
        }

        const quantity = Number(authorisedQuantity);

        if (
          !Number.isFinite(quantity) ||
          quantity <= 0
        ) {
          alert(
            'Authorised quantity must be greater than zero.'
          );
          return;
        }

        if (new Date(validUntil) < new Date(validFrom)) {
          alert(
            'The expiry date must be later than the commencement date.'
          );
          return;
        }

        setBusy(true);

        const imageCID = await uploadToIPFS(imageFile);

        const assetMetadata = {
          name: tokenName.trim(),
          description: tokenDescription.trim(),

          image:
            `https://gateway.pinata.cloud/ipfs/${imageCID}`,

          authorisedProduct: authorisedProduct.trim(),
          authorisedQuantity: quantity,
          unit: quantityUnit,
          validFrom,
          validUntil
        };
      
      const metadataCID = await uploadMetadataToIPFS(assetMetadata);
      setAssetMetaIPFS(`ipfs://${metadataCID}`);
      setMessage('✅ Asset metadata uploaded to IPFS.');
    } catch (error) {
      console.error(error);
      setMessage('❌ ' + (error.message || 'Error uploading asset metadata'));
    } finally {
      setBusy(false);
    }
  };

  // ---- Propose mint ----
  const proposeMint = async () => {
    const count = parseInt(numTokens, 10) || 0;
    if (!assetMetaIPFS || count < 1) {
      alert('Asset Meta IPFS and a valid number of tokens are required.');
      return;
    }

    try {
      setBusy(true);
      setMessage('Preparing transaction...');
      const { web3, account } = await ensureWallet();
      const contract = new web3.eth.Contract(contractABI, contractAddress);
      const gasPrice = await web3.eth.getGasPrice();

      const gasLimit = await contract.methods
        .proposeMint(account, assetMetaIPFS)
        .estimateGas({ from: account });

      for (let i = 0; i < count; i += 1) {
        await contract.methods
          .proposeMint(account, assetMetaIPFS)
          .send({ from: account, gas: gasLimit, gasPrice });
      }

      setMessage('✅ Mint proposal(s) submitted! Awaiting multisig approvals.');
    } catch (error) {
      console.error('Error proposing mint:', error);
      if (error && error.code === -32002) {
        setMessage('❌ A MetaMask request is already pending. Open MetaMask to approve/cancel it, then try again.');
      } else {
        setMessage('❌ Failed to propose mint: ' + (error.message || error));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="container" style={{ marginBottom: "30px" }}>
      {/* ✅ Add this new collection summary section */}
        <h2>About Digital Authorisation Licences</h2>
        <p> Each licence records the holder, permitted use, conditions and validity period for an asset, 
          such as intellectual property, a trademark or a design.
        </p>
        <p>All Digital Authorisation Licences are issued and managed through this  
          <strong> blockchain smart contract:</strong>{' '}
          <a
            href={`https://amoy.polygonscan.com/address/${contractAddress}`}
            target="_blank"
            rel="noreferrer"
          >
            {contractAddress}
          </a>
        </p>

      {/* Minting Sections */}
      <section className="token-section">
        <h3>Enter Licence Information</h3>
        <p>Provide the asset and authorisation details. Once the information is successfully stored on IPFS, 
          the Digital Authorisation Licence can be issued.</p>

        <div className="form-group">
          <label>Licence Name:</label>
          <input type="text" value={tokenName} onChange={handleTokenNameChange} placeholder="e.g., xxx licence" />
        </div>

        <div className="form-group">
          <label>Licence Description:</label>
          <textarea value={tokenDescription} onChange={handleTokenDescriptionChange} placeholder="This xxx licence represents the ownership, custodianship or rights of xxx owned by xxx entity" />
        </div>

        <div className="form-group">
            <label>Licence Image:</label>

            <div className="image-input-options">
                <div>
                    <label htmlFor="totoken-image-upload" className="image-action-label">
                        Choose Image
                    </label>

                    <input
                        id="totoken-image-upload"
                        type="file"
                        accept="image/jpeg,image/png"
                        onChange={handleImageChange}
                    />
                </div>

                <div>
                    <label htmlFor="totoken-image-camera" className="image-action-label">
                        Take Photo
                    </label>

                    <input
                        id="totoken-image-camera"
                        type="file"
                        accept="image/*"
                        capture="environment"
                        onChange={handleImageChange}
                    />
                </div>
            </div>

            {imagePreview && (
                <div className="image-preview">
                    <p>Selected licence image:</p>
                    <img
                        src={imagePreview}
                        alt="TOToken preview"
                    />
                    <p>{imageFile?.name}</p>
                </div>
            )}
        </div>

        <label htmlFor="authorisedProduct">
            Authorised Product:
          </label>
          <input
            id="authorisedProduct"
            value={authorisedProduct}
            onChange={(e) => setAuthorisedProduct(e.target.value)}
            placeholder="e.g., Native violet"
            required
          />

          <label htmlFor="authorisedQuantity">
            Authorised Quantity:
          </label>
          <input
            id="authorisedQuantity"
            type="number"
            min="0"
            value={authorisedQuantity}
            onChange={(e) => setAuthorisedQuantity(e.target.value)}
            placeholder="e.g., 500"
            required
          />

          <label htmlFor="quantityUnit">
            Quantity Unit:
          </label>
          <select
            id="quantityUnit"
            value={quantityUnit}
            onChange={(e) => setQuantityUnit(e.target.value)}
            required
          >
            <option value="">Select a unit</option>
            <option value="kg">kg</option>
            <option value="tonnes">tonnes</option>
            <option value="units">units</option>
            <option value="litres">litres</option>
          </select>

          <label htmlFor="validFrom">Valid From:</label>
          <input
            id="validFrom"
            type="date"
            value={validFrom}
            onChange={(e) => setValidFrom(e.target.value)}
            required
          />

          <label htmlFor="validUntil">Valid Until:</label>
          <input
            id="validUntil"
            type="date"
            value={validUntil}
            onChange={(e) => setValidUntil(e.target.value)}
            required
          />

        <button 
          type="button"
          className="primary-action-button"
          onClick={generateAssetMetaIPFS} disabled={busy}>
           {busy ? 'Working…' : 'Generate Licence Record & Identifier'}
        </button>

        {assetMetaIPFS && (
          <p>
            Asset Metadata IPFS:{' '}
            <a
              href={`https://gateway.pinata.cloud/ipfs/${assetMetaIPFS.replace('ipfs://', '')}`}
              target="_blank"
              rel="noreferrer"
            >
              {assetMetaIPFS}
            </a>
          </p>
        )}
      </section>

      <section className="mint-section">
        <h2>Propose to Issue Licence</h2>
        <p>The licences will be created after receiving the required approvals from authorised signers.</p>

        <div className="form-group">
        <label>
          Number of Licences to Issue:
          <input type="number" value={numTokens} onChange={handleNumTokensChange} min="1" />
        </label>

        <button
          type="button"
          className="primary-action-button"
          onClick={proposeMint}
          disabled={!assetMetaIPFS || busy}
          style={{ marginBottom: '30px' }}
        >
          {busy ? 'Submitting…' : 'Submit Licence Proposal'}
        </button>

        {message && <p style={{ marginBottom: '30px' }}>{message}</p>}
        </div>
      </section>
    </div>
  );
};

export default ProposeTokenMint;